import { useId, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/ui/utils/main'

interface CollapsibleSectionProps {
  title: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  className?: string
}

/**
 * A collapsible section whose content is always mounted in the DOM — only
 * its visibility is toggled with `hidden` / `print:block`. This is
 * deliberately different from an ad-hoc `{open && <Content />}` pattern (see
 * `BotPanelSection`'s `CollapsibleCard`): unmounting on collapse would mean
 * printing (`window.print()`) could only ever show whatever was expanded at
 * the time, since jsdom/the browser can't "expand" a section just for
 * print. Keeping the content mounted and switching classes lets the
 * `@media print` stylesheet force every section open on the printed page
 * regardless of its on-screen collapsed state.
 */
export function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
  className,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const contentId = useId()

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-controls={contentId}
        className="flex w-full items-center justify-between gap-2 py-2 text-left"
      >
        <span className="font-medium text-foreground">{title}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'w-4 h-4 shrink-0 text-muted-foreground transition-transform print:hidden',
            open && 'rotate-180',
          )}
        />
      </button>
      <div id={contentId} className={open ? 'block' : 'hidden print:block'}>
        {children}
      </div>
    </div>
  )
}
